import { useState, useEffect, createContext, useContext } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Toaster } from '@/components/ui/sonner';
import { toast } from 'sonner';
import ErrorBoundary from '@/components/ErrorBoundary';
import AuthPage from '@/pages/AuthPage';
import Dashboard from '@/pages/Dashboard';
import SnapshotAnalysis from '@/pages/SnapshotAnalysis';
import SnapshotBenefits from '@/pages/SnapshotBenefits';
import GrowthModelBenefits from '@/pages/GrowthModelBenefits';
import SFRCalculatorBenefits from '@/pages/SFRCalculatorBenefits';
import GrowthPrognosisBenefits from '@/pages/GrowthPrognosisBenefits';
import GrowthModelAnalysis from '@/pages/GrowthModelAnalysis';
import GrowthPrognosis from '@/pages/GrowthPrognosis';
import HistoryPage from '@/pages/HistoryPage';
import AnalysisDetail from '@/pages/AnalysisDetail';
import SFRCalculator from '@/pages/SFRCalculator';
import AdminConsole from '@/pages/AdminConsole';
import SpreadsheetGrowthComparison from '@/pages/SpreadsheetGrowthComparison';
import SpreadsheetBenefits from '@/pages/SpreadsheetBenefits';
import CustomModelBenefits from '@/pages/CustomModelBenefits';
import TemperatureModelBenefits from '@/pages/TemperatureModelBenefits';
import MultiProfileComparison from '@/pages/MultiProfileComparison';
import MultiProfileBenefits from '@/pages/MultiProfileBenefits';
import DataSecurity from '@/pages/DataSecurity';
import WingmanChat from '@/components/WingmanChat';
import ContactAdmin from '@/components/ContactAdmin';
import { RestrictedDemoShield } from '@/components/RestrictedDemoShield';

import { ReportVisibilityProvider } from '@/contexts/ReportVisibilityContext';
import { CreditsProvider } from '@/contexts/CreditsContext';
import { ThemeProvider } from '@/contexts/ThemeContext';
import BuyCreditsModal from '@/components/BuyCreditsModal';
import BillingPage from '@/pages/BillingPage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Create axios instance with interceptors for token expiry handling
const api = axios.create({
  baseURL: API,
});

// Track if we're already handling a 401 to prevent multiple redirects
let isHandling401 = false;

// Setup axios interceptor for 401 responses (token expiry)
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 && !isHandling401) {
      isHandling401 = true;
      // Token expired or invalid - clear auth and redirect
      localStorage.removeItem('token');
      toast.error('Session expired. Please log in again.');
      // Small delay to allow toast to show, then reload
      setTimeout(() => {
        isHandling401 = false;
        window.location.href = '/auth';
      }, 1000);
    }
    return Promise.reject(error);
  }
);

// Export for use in other components
export { api, API };

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  // Set default auth header when token changes
  useEffect(() => {
    if (token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      delete api.defaults.headers.common['Authorization'];
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await api.get('/auth/me');
      setUser(response.data);
    } catch (error) {
      // 401 is handled by interceptor, other errors trigger logout
      if (error.response?.status !== 401) {
        logout();
      }
    } finally {
      setLoading(false);
    }
  };

  // Heartbeat to track user activity (every 60 seconds)
  useEffect(() => {
    if (!token || !user) return;
    
    const sendHeartbeat = async () => {
      try {
        await api.post('/auth/heartbeat', {});
      } catch (error) {
        // Silently ignore heartbeat errors (401 handled by interceptor)
      }
    };

    // Send initial heartbeat
    sendHeartbeat();

    // Set up interval for heartbeat
    const interval = setInterval(sendHeartbeat, 60000);
    
    return () => clearInterval(interval);
  }, [token, user]);

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      const { access_token, user } = response.data;
      localStorage.setItem('token', access_token);
      // Fresh login → let the onboarding modal re-evaluate (it tracks its own
      // seen-state via server `onboarding_video_seen_at` + sessionStorage).
      try { sessionStorage.removeItem('pfd_onboarding_shown_this_session'); } catch { /* ignore */ }
      setToken(access_token);
      setUser(user);
      toast.success('Login successful!');
      return { success: true };
    } catch (error) {
      const detail = error.response?.data?.detail || 'Login failed';
      const status = error.response?.status;
      toast.error(detail);
      return { success: false, detail, status };
    }
  };

  const register = async (firstName, lastName, company, email, password) => {
    try {
      const response = await axios.post(`${API}/auth/register`, { 
        first_name: firstName, 
        last_name: lastName, 
        company, 
        email, 
        password 
      });
      const { access_token, user } = response.data;
      localStorage.setItem('token', access_token);
      try { sessionStorage.removeItem('pfd_onboarding_shown_this_session'); } catch { /* ignore */ }
      setToken(access_token);
      setUser(user);
      toast.success('Registration successful!');
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
      return false;
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    try { sessionStorage.removeItem('pfd_onboarding_shown_this_session'); } catch { /* ignore */ }
    setToken(null);
    setUser(null);
    delete api.defaults.headers.common['Authorization'];
    toast.success('Logged out successfully');
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, token, refreshUser: fetchUser }}>
      {children}
    </AuthContext.Provider>
  );
}

function PrivateRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  return user ? children : <Navigate to="/auth" />;
}

// SECURITY: Admin route wrapper - only allows totalproductionsolutions@gmail.com
const ADMIN_EMAIL = 'totalproductionsolutions@gmail.com';

function AdminRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  // SECURITY: Strict check - both is_admin flag AND exact email match required
  const isAdmin = user?.is_admin === true && user?.email?.toLowerCase() === ADMIN_EMAIL.toLowerCase();
  
  if (!user) return <Navigate to="/auth" />;
  if (!isAdmin) return <Navigate to="/" />;
  
  return children;
}

function App() {
  return (
    <ErrorBoundary>
    <ThemeProvider>
    <AuthProvider>
      <CreditsProvider>
      <ReportVisibilityProvider>
      <div className="App">
        <BrowserRouter>
          <BuyCreditsModal />
          <Routes>
            <Route path="/auth" element={<AuthPage />} />
            <Route
              path="/billing"
              element={
                <PrivateRoute>
                  <BillingPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/"
              element={
                <PrivateRoute>
                  <Dashboard />
                </PrivateRoute>
              }
            />
            <Route
              path="/dashboard"
              element={
                <PrivateRoute>
                  <Dashboard />
                </PrivateRoute>
              }
            />
            <Route
              path="/snapshot"
              element={
                <PrivateRoute>
                  <SnapshotAnalysis />
                </PrivateRoute>
              }
            />
            <Route
              path="/snapshot/benefits"
              element={
                <PrivateRoute>
                  <SnapshotBenefits />
                </PrivateRoute>
              }
            />
            <Route
              path="/growth-model"
              element={
                <PrivateRoute>
                  <GrowthModelAnalysis />
                </PrivateRoute>
              }
            />
            <Route
              path="/growth-model/benefits"
              element={
                <PrivateRoute>
                  <GrowthModelBenefits />
                </PrivateRoute>
              }
            />
            <Route
              path="/history"
              element={
                <PrivateRoute>
                  <HistoryPage />
                </PrivateRoute>
              }
            />
            <Route
              path="/analysis/:id"
              element={
                <PrivateRoute>
                  <AnalysisDetail />
                </PrivateRoute>
              }
            />
            <Route
              path="/sfr-calculator"
              element={
                <PrivateRoute>
                  <SFRCalculator />
                </PrivateRoute>
              }
            />
            <Route
              path="/sfr-calculator/benefits"
              element={
                <PrivateRoute>
                  <SFRCalculatorBenefits />
                </PrivateRoute>
              }
            />
            <Route
              path="/growth-prognosis"
              element={
                <PrivateRoute>
                  <GrowthPrognosis />
                </PrivateRoute>
              }
            />
            <Route
              path="/growth-prognosis/benefits"
              element={
                <PrivateRoute>
                  <GrowthPrognosisBenefits />
                </PrivateRoute>
              }
            />
            <Route
              path="/spreadsheet-comparison"
              element={
                <PrivateRoute>
                  <SpreadsheetGrowthComparison />
                </PrivateRoute>
              }
            />
            <Route
              path="/spreadsheet-comparison/benefits"
              element={
                <PrivateRoute>
                  <SpreadsheetBenefits />
                </PrivateRoute>
              }
            />
            <Route
              path="/multi-profile"
              element={
                <PrivateRoute>
                  <MultiProfileComparison />
                </PrivateRoute>
              }
            />
            <Route
              path="/multi-profile/benefits"
              element={
                <PrivateRoute>
                  <MultiProfileBenefits />
                </PrivateRoute>
              }
            />
            <Route
              path="/custom-model/benefits"
              element={
                <PrivateRoute>
                  <CustomModelBenefits />
                </PrivateRoute>
              }
            />
            <Route
              path="/temperature-model/benefits"
              element={
                <PrivateRoute>
                  <TemperatureModelBenefits />
                </PrivateRoute>
              }
            />
            <Route
              path="/data-security"
              element={<DataSecurity />}
            />
            <Route
              path="/admin"
              element={
                <AdminRoute>
                  <AdminConsole />
                </AdminRoute>
              }
            />

          </Routes>
          {/* Global footer */}
          <footer className="border-t border-slate-200 bg-white mt-8">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between text-xs text-slate-400">
              <span>PFD Analysis</span>
              <a href="/data-security" className="flex items-center gap-1 text-slate-400 hover:text-teal-600 transition-colors" data-testid="footer-security-link">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                Data Security
              </a>
            </div>
          </footer>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
        <WingmanChat />
        <ContactAdmin />
        <RestrictedDemoShield />
      </div>
      </ReportVisibilityProvider>
      </CreditsProvider>
    </AuthProvider>
    </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;